import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChainPicker, Chain, loadChain, saveChain } from '../../components/common/ChainPicker';
import { DatePicker } from '../../components/common/DatePicker';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';
import ImageIcon from '@mui/icons-material/Image';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
type Tab = 'snapshot' | 'activity';

const mono: React.CSSProperties = { fontFamily: 'var(--font-mono)' };
const monoB: React.CSSProperties = { fontFamily: 'var(--font-mono)', fontWeight: 600 };

// ===== SNAPSHOT DATA =====
const UPC_BUCKETS = [
  { bucket: '0 crops', upcs: 342, pct: 8.5 },
  { bucket: '[1, 5)', upcs: 891, pct: 22.2 },
  { bucket: '[5, 10)', upcs: 1204, pct: 29.9 },
  { bucket: '[10, 20)', upcs: 876, pct: 21.8 },
  { bucket: '[20, 30)', upcs: 423, pct: 10.5 },
  { bucket: '[30, 50)', upcs: 198, pct: 4.9 },
  { bucket: '[50, \u221E)', upcs: 87, pct: 2.2 },
];

function upcBucketColor(bucket: string): string {
  if (bucket === '0 crops') return 'var(--danger)';
  if (bucket === '[1, 5)') return 'var(--warning)';
  if (bucket === '[5, 10)' || bucket === '[10, 20)') return 'var(--positive)';
  if (bucket === '[20, 30)') return 'var(--blue)';
  return 'var(--purple)';
}

const VARIANT_BUCKETS = [
  { bucket: '1 crop', variants: 891, pct: 24.7 },
  { bucket: '[2, 5)', variants: 1042, pct: 28.8 },
  { bucket: '[5, 10)', variants: 823, pct: 22.8 },
  { bucket: '[10, 20)', variants: 534, pct: 14.8 },
  { bucket: '[20, 30)', variants: 218, pct: 6.0 },
  { bucket: '[30, 50)', variants: 79, pct: 2.2 },
  { bucket: '[50, \u221E)', variants: 25, pct: 0.7 },
];

function variantBucketColor(bucket: string): string {
  if (bucket === '1 crop') return 'var(--danger)';
  if (bucket === '[2, 5)') return 'var(--warning)';
  if (bucket === '[5, 10)' || bucket === '[10, 20)') return 'var(--positive)';
  if (bucket === '[20, 30)') return 'var(--blue)';
  return 'var(--purple)';
}

const IMAGE_OUTLIERS: { metric: string; samples: { value: string }[] }[] = [
  { metric: 'Overexposed', samples: [{ value: '0.96' }, { value: '0.94' }, { value: '0.93' }, { value: '0.91' }, { value: '0.90' }] },
  { metric: 'Underexposed', samples: [{ value: '0.03' }, { value: '0.04' }, { value: '0.05' }, { value: '0.06' }, { value: '0.07' }] },
  { metric: 'Black mask', samples: [{ value: '94.2%' }, { value: '91.8%' }, { value: '89.3%' }, { value: '87.1%' }, { value: '85.6%' }] },
  { metric: 'Blurry', samples: [{ value: '0.18' }, { value: '0.21' }, { value: '0.23' }, { value: '0.25' }, { value: '0.27' }] },
  { metric: 'Extreme aspect ratio', samples: [{ value: '6.8:1' }, { value: '5.9:1' }, { value: '5.4:1' }, { value: '5.1:1' }, { value: '4.8:1' }] },
];

interface StoreRow { name: string; chain: string; mods: number; whitelisted: number; cov1: number; cov5: number }

const STORE_COVERAGE: StoreRow[] = [
  { name: 'Lidl Manchester #155', chain: 'Lidl GB', mods: 24, whitelisted: 812, cov1: 94.2, cov5: 71.3 },
  { name: 'Lidl Birmingham #203', chain: 'Lidl GB', mods: 18, whitelisted: 743, cov1: 91.8, cov5: 68.9 },
  { name: 'Lidl London Central #87', chain: 'Lidl GB', mods: 32, whitelisted: 1021, cov1: 96.1, cov5: 82.4 },
  { name: 'Lidl Edinburgh #307', chain: 'Lidl GB', mods: 20, whitelisted: 678, cov1: 89.4, cov5: 64.2 },
  { name: 'Lidl Hamburg #87', chain: 'Lidl DE', mods: 26, whitelisted: 903, cov1: 93.5, cov5: 76.8 },
  { name: 'Lidl Berlin #53', chain: 'Lidl DE', mods: 22, whitelisted: 841, cov1: 91.2, cov5: 72.1 },
  { name: 'Lidl München #112', chain: 'Lidl DE', mods: 19, whitelisted: 762, cov1: 88.9, cov5: 65.4 },
  { name: 'Morrisons Leeds #334', chain: 'Morrisons', mods: 28, whitelisted: 934, cov1: 88.7, cov5: 62.1 },
  { name: 'Morrisons Sheffield #271', chain: 'Morrisons', mods: 22, whitelisted: 867, cov1: 92.3, cov5: 74.6 },
  { name: 'Morrisons Camden #201', chain: 'Morrisons', mods: 25, whitelisted: 891, cov1: 90.1, cov5: 67.8 },
  { name: 'Asda Bristol #67', chain: 'Asda', mods: 20, whitelisted: 756, cov1: 85.4, cov5: 58.3 },
  { name: 'Asda Cardiff #119', chain: 'Asda', mods: 16, whitelisted: 691, cov1: 79.2, cov5: 51.7 },
  { name: 'Asda Leyton #44', chain: 'Asda', mods: 18, whitelisted: 723, cov1: 82.6, cov5: 55.1 },
];

interface ConfidenceRow { store: string; chain: string; samples: number; avgTop1: number; avgDelta: number }

const CONFIDENCE_DATA: ConfidenceRow[] = [
  { store: 'Asda Cardiff #119', chain: 'Asda', samples: 1243, avgTop1: 0.721, avgDelta: 0.089 },
  { store: 'Asda Bristol #67', chain: 'Asda', samples: 1567, avgTop1: 0.748, avgDelta: 0.102 },
  { store: 'Asda Leyton #44', chain: 'Asda', samples: 1398, avgTop1: 0.763, avgDelta: 0.112 },
  { store: 'Morrisons Leeds #334', chain: 'Morrisons', samples: 1891, avgTop1: 0.782, avgDelta: 0.134 },
  { store: 'Morrisons Sheffield #271', chain: 'Morrisons', samples: 1654, avgTop1: 0.801, avgDelta: 0.141 },
  { store: 'Morrisons Camden #201', chain: 'Morrisons', samples: 1723, avgTop1: 0.814, avgDelta: 0.152 },
  { store: 'Lidl Manchester #155', chain: 'Lidl GB', samples: 2103, avgTop1: 0.834, avgDelta: 0.167 },
  { store: 'Lidl Birmingham #203', chain: 'Lidl GB', samples: 1876, avgTop1: 0.851, avgDelta: 0.178 },
  { store: 'Lidl Edinburgh #307', chain: 'Lidl GB', samples: 1934, avgTop1: 0.862, avgDelta: 0.184 },
  { store: 'Lidl London Central #87', chain: 'Lidl GB', samples: 2567, avgTop1: 0.891, avgDelta: 0.213 },
  { store: 'Lidl Hamburg #87', chain: 'Lidl DE', samples: 2234, avgTop1: 0.873, avgDelta: 0.192 },
  { store: 'Lidl Berlin #53', chain: 'Lidl DE', samples: 2102, avgTop1: 0.878, avgDelta: 0.196 },
  { store: 'Lidl München #112', chain: 'Lidl DE', samples: 1945, avgTop1: 0.865, avgDelta: 0.188 },
];

function confidenceColor(val: number): string {
  if (val >= 0.85) return 'var(--positive)';
  if (val >= 0.75) return 'var(--warning)';
  return 'var(--danger)';
}

// ===== ACTIVITY DATA =====

const ACTIVITY_ROWS = [
  { action: 'crops_added', count: 1247, vsYesterday: +312 },
  { action: 'crops_deactivated', count: 89, vsYesterday: -14 },
  { action: 'variants_created', count: 34, vsYesterday: +8 },
  { action: 'variants_with_verified_barcode', count: 12, vsYesterday: +3 },
  { action: 'representatives_updated', count: 7, vsYesterday: -2 },
  { action: 'tasks_completed', count: 312, vsYesterday: +45 },
  { action: 'tasks_skipped', count: 28, vsYesterday: -6 },
];

// ===== NEW DUPLICATES & LOW COHESION =====
const NEW_DUPLICATES = [
  { variantA: 'd6bb0cd5', variantB: 'a3f21e8c', upc: '5449000000996', product: 'Coca-Cola 330ml', similarity: 0.987, type: 'intra-variant' as const },
  { variantA: '7c4e9f12', variantB: 'e8a52d91', upc: '4060800100078', product: 'Pepsi 500ml', similarity: 0.943, type: 'inter-variant' as const },
  { variantA: 'd6bb0cd5', variantB: '7c4e9f12', upc: '5449000000996', product: 'Coca-Cola vs Pepsi', similarity: 0.912, type: 'cross-upc' as const },
];

function dupTypeColor(type: string): string {
  if (type === 'cross-upc') return 'var(--danger)';
  if (type === 'inter-variant') return 'var(--warning)';
  return 'var(--text-secondary)';
}

function masterLabelLink(itemNumber: string, highlight: string): string {
  return `/forge/admin/master-label?item_number=${encodeURIComponent(itemNumber)}&h=${encodeURIComponent(highlight)}`;
}

const NEW_STORES_ONBOARDED = [
  { store: 'Lidl Hamburg #291', chain: 'Lidl DE', mods: 22, whitelisted: 834, date: '2026-03-12' },
  { store: 'Asda Cardiff #119', chain: 'Asda', mods: 16, whitelisted: 691, date: '2026-03-12' },
];

const INFERENCE_DROPS = [
  { store: 'Lidl Manchester #155', chain: 'Lidl GB', prevTop1: 0.906, currTop1: 0.834, delta: -0.072, modsAffected: 24 },
  { store: 'Morrisons Leeds #334', chain: 'Morrisons', prevTop1: 0.862, currTop1: 0.812, delta: -0.050, modsAffected: 18 },
  { store: 'Asda Cardiff #119', chain: 'Asda', prevTop1: 0.791, currTop1: 0.748, delta: -0.043, modsAffected: 12 },
];

function covColor(pct: number): string {
  if (pct >= 90) return 'var(--positive)';
  if (pct >= 70) return 'var(--warning)';
  return 'var(--danger)';
}

function diffDisplay(val: number): React.ReactNode {
  const sign = val > 0 ? '+' : '';
  const color = val > 0 ? 'var(--positive)' : val < 0 ? 'var(--danger)' : 'var(--text-secondary)';
  return <span style={{ ...mono, color }}>{sign}{val}</span>;
}

// ===== COMPONENT =====
export const DailyReportDashboard: React.FC = () => {
  const [chain, setChain] = useState<Chain>(loadChain);
  const [date, setDate] = useState('2026-03-12');
  const [tab, setTab] = useState<Tab>('snapshot');
  const [coverageSearch, setCoverageSearch] = useState('');
  const [confidenceSearch, setConfidenceSearch] = useState('');
  const visibility = useStaggeredEntrance(8, 20);

  const handleChainChange = (val: Chain) => {
    setChain(val);
    saveChain(val);
    setCoverageSearch('');
    setConfidenceSearch('');
  };

  const upcTotal = UPC_BUCKETS.reduce((s, b) => s + b.upcs, 0);

  const filteredStores = useMemo(() => {
    let rows = chain === 'Global' ? STORE_COVERAGE : STORE_COVERAGE.filter((s) => s.chain === chain);
    if (coverageSearch.trim()) {
      const q = coverageSearch.toLowerCase();
      rows = rows.filter((s) => s.name.toLowerCase().includes(q));
    }
    return rows;
  }, [chain, coverageSearch]);

  const filteredConfidence = useMemo(() => {
    let rows = chain === 'Global' ? CONFIDENCE_DATA : CONFIDENCE_DATA.filter((s) => s.chain === chain);
    if (confidenceSearch.trim()) {
      const q = confidenceSearch.toLowerCase();
      rows = rows.filter((s) => s.store.toLowerCase().includes(q));
    }
    return rows;
  }, [chain, confidenceSearch]);

  return (
    <div className="page-content">
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <ChainPicker value={chain} onChange={handleChainChange} />
        <DatePicker value={date} onChange={setDate} />
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {(['snapshot', 'activity'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              background: 'none', border: 'none', color: tab === t ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 14, fontWeight: 600, padding: '10px 20px', cursor: 'pointer',
              borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1, textTransform: 'capitalize',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Tab: Snapshot */}
      {tab === 'snapshot' && (
        <>
          {/* UPC Coverage Table */}
          <div style={{ opacity: visibility[0] ? 1 : 0, transform: visibility[0] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div className="forge-card" style={{ marginBottom: 20 }}>
              <div className="forge-card-title">UPC Coverage — Bucket Distribution</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Bucket</th><th>UPCs</th><th>%</th><th>Cumulative %</th></tr>
                </thead>
                <tbody>
                  {UPC_BUCKETS.reduce<{ rows: React.ReactNode[]; cumPct: number }>((acc, b) => {
                    const cum = acc.cumPct + b.pct;
                    const color = upcBucketColor(b.bucket);
                    acc.rows.push(
                      <tr key={b.bucket}>
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8, verticalAlign: 'middle' }} />
                          {b.bucket}
                        </td>
                        <td style={mono}>{b.upcs.toLocaleString()}</td>
                        <td style={{ ...mono, color }}>{b.pct.toFixed(1)}%</td>
                        <td style={mono}>{cum.toFixed(1)}%</td>
                      </tr>
                    );
                    acc.cumPct = cum;
                    return acc;
                  }, { rows: [], cumPct: 0 }).rows}
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <td>Total</td>
                    <td style={monoB}>{upcTotal.toLocaleString()}</td>
                    <td style={monoB}>100%</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Variant Coverage — Bucket Distribution */}
          <div style={{ opacity: visibility[1] ? 1 : 0, transform: visibility[1] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div className="forge-card" style={{ marginBottom: 20 }}>
              <div className="forge-card-title">Variant Coverage — Bucket Distribution</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Bucket</th><th>Variants</th><th>%</th><th>Cumulative %</th></tr>
                </thead>
                <tbody>
                  {VARIANT_BUCKETS.reduce<{ rows: React.ReactNode[]; cumPct: number }>((acc, b) => {
                    const cum = acc.cumPct + b.pct;
                    const color = variantBucketColor(b.bucket);
                    acc.rows.push(
                      <tr key={b.bucket}>
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8, verticalAlign: 'middle' }} />
                          {b.bucket}
                        </td>
                        <td style={mono}>{b.variants.toLocaleString()}</td>
                        <td style={{ ...mono, color }}>{b.pct.toFixed(1)}%</td>
                        <td style={mono}>{cum.toFixed(1)}%</td>
                      </tr>
                    );
                    acc.cumPct = cum;
                    return acc;
                  }, { rows: [], cumPct: 0 }).rows}
                  <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                    <td>Total</td>
                    <td style={monoB}>{VARIANT_BUCKETS.reduce((s, b) => s + b.variants, 0).toLocaleString()}</td>
                    <td style={monoB}>100%</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Quality Metrics */}
          <div style={{ opacity: visibility[2] ? 1 : 0, transform: visibility[2] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14, color: 'var(--text-secondary)' }}>Quality Metrics</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
              <div className="forge-card">
                <div className="forge-card-title">Likely Duplicates</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Intra-variant</span>
                    <span style={monoB}>234</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Inter-variant</span>
                    <span style={monoB}>101</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Cross-UPC</span>
                    <span style={{ ...monoB, color: 'var(--danger)' }}>12</span>
                  </div>
                </div>
              </div>

              <div className="forge-card">
                <div className="forge-card-title">Cohesion</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Avg cohesion</span>
                    <span style={monoB}>0.847</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Below 0.7 threshold</span>
                    <span style={{ ...monoB, color: 'var(--warning)' }}>143 variants (3.9%)</span>
                  </div>
                </div>
              </div>

              <div className="forge-card">
                <div className="forge-card-title">Capping</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Exceeding 30 cap</span>
                    <span style={{ ...monoB, color: 'var(--warning)' }}>67</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Largest variant</span>
                    <span style={monoB}>58 crops</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Store UPC Coverage Table */}
          <div style={{ opacity: visibility[4] ? 1 : 0, transform: visibility[4] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div className="forge-card" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="forge-card-title" style={{ marginBottom: 0 }}>Store UPC Coverage</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px',
                  }}>
                    <SearchOutlinedIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={coverageSearch}
                      onChange={(e) => setCoverageSearch(e.target.value)}
                      style={{
                        background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)',
                        fontSize: 12, width: 120,
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                    {filteredStores.length} stores
                  </span>
                </div>
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Store Name</th>
                    {chain === 'Global' && <th>Chain</th>}
                    <th>Active Mods</th><th>Whitelisted UPCs</th>
                    <th>Coverage &ge;1</th><th>Coverage &ge;5</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStores.map((s) => (
                    <tr key={s.name}>
                      <td style={{ fontWeight: 600 }}>{s.name}</td>
                      {chain === 'Global' && <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{s.chain}</td>}
                      <td style={mono}>{s.mods}</td>
                      <td style={mono}>{s.whitelisted.toLocaleString()}</td>
                      <td style={{ ...mono, color: covColor(s.cov1) }}>{s.cov1.toFixed(1)}%</td>
                      <td style={{ ...mono, color: covColor(s.cov5) }}>{s.cov5.toFixed(1)}%</td>
                    </tr>
                  ))}
                  {filteredStores.length === 0 && (
                    <tr><td colSpan={chain === 'Global' ? 6 : 5} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 20 }}>No stores match filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inference Statistics Table */}
          <div style={{ opacity: visibility[5] ? 1 : 0, transform: visibility[5] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div className="forge-card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div className="forge-card-title" style={{ marginBottom: 0 }}>Inference Statistics</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 8px',
                  }}>
                    <SearchOutlinedIcon style={{ fontSize: 14, color: 'var(--text-tertiary)' }} />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={confidenceSearch}
                      onChange={(e) => setConfidenceSearch(e.target.value)}
                      style={{
                        background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)',
                        fontSize: 12, width: 120,
                      }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                    {filteredConfidence.length} stores
                  </span>
                </div>
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Store</th>
                    {chain === 'Global' && <th>Chain</th>}
                    <th>Samples</th><th>Avg Top-1</th><th>Avg Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConfidence.map((s) => (
                    <tr key={s.store}>
                      <td style={{ fontWeight: 600 }}>{s.store}</td>
                      {chain === 'Global' && <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{s.chain}</td>}
                      <td style={mono}>{s.samples.toLocaleString()}</td>
                      <td style={{ ...mono, color: confidenceColor(s.avgTop1), fontWeight: s.avgTop1 < 0.75 ? 600 : undefined }}>
                        {s.avgTop1.toFixed(3)}
                      </td>
                      <td style={mono}>{s.avgDelta.toFixed(3)}</td>
                    </tr>
                  ))}
                  {filteredConfidence.length === 0 && (
                    <tr><td colSpan={chain === 'Global' ? 5 : 4} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 20 }}>No stores match filter</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Tab: Activity */}
      {tab === 'activity' && (
        <>
          <div style={{ opacity: visibility[0] ? 1 : 0, transform: visibility[0] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease' }}>
            <div className="forge-card">
              <div className="forge-card-title">Activity Detail</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Action</th><th>Count</th><th>vs Yesterday</th></tr>
                </thead>
                <tbody>
                  {ACTIVITY_ROWS.map((r) => (
                    <tr key={r.action}>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>{r.action}</td>
                      <td style={mono}>{r.count.toLocaleString()}</td>
                      <td>{diffDisplay(r.vsYesterday)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* New Likely Duplicates */}
          <div style={{ opacity: visibility[1] ? 1 : 0, transform: visibility[1] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease', marginTop: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">New Likely Duplicates</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Product</th><th>Variant A</th><th>Variant B</th><th>Similarity</th><th>Type</th><th></th></tr>
                </thead>
                <tbody>
                  {NEW_DUPLICATES.map((d, i) => (
                    <tr key={i}>
                      <td>{d.product}</td>
                      <td style={mono}>{d.variantA.slice(0, 8)}</td>
                      <td style={mono}>{d.variantB.slice(0, 8)}</td>
                      <td style={{ ...mono, color: d.similarity > 0.95 ? 'var(--danger)' : 'var(--warning)' }}>{d.similarity.toFixed(3)}</td>
                      <td>
                        <span style={{ fontSize: 11, fontWeight: 600, color: dupTypeColor(d.type) }}>
                          {d.type.replace('-', ' ')}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={masterLabelLink(d.upc, d.variantA)}
                          style={{ color: 'var(--accent)', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12 }}
                        >
                          View <OpenInNewIcon style={{ fontSize: 13 }} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* New Image Quality Outliers */}
          <div style={{ opacity: visibility[2] ? 1 : 0, transform: visibility[2] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease', marginTop: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">New Image Quality Outliers</div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    {[1, 2, 3, 4, 5].map((n) => <th key={n} style={{ textAlign: 'center' }}>#{n}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {IMAGE_OUTLIERS.map((row) => (
                    <tr key={row.metric}>
                      <td style={{ fontWeight: 600 }}>{row.metric}</td>
                      {row.samples.map((s, i) => (
                        <td key={i} style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                            <div style={{
                              width: 40, height: 40, borderRadius: 4, background: 'var(--elevated)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <ImageIcon style={{ color: 'var(--text-tertiary)', fontSize: 18 }} />
                            </div>
                            <span style={{ ...mono, fontSize: 11, color: 'var(--warning)' }}>{s.value}</span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Inference Confidence Drops */}
          <div style={{ opacity: visibility[3] ? 1 : 0, transform: visibility[3] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease', marginTop: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">Inference Confidence Drops</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Store</th><th>Chain</th><th>Previous Top-1</th><th>Current Top-1</th><th>Delta</th><th>Mods Affected</th></tr>
                </thead>
                <tbody>
                  {INFERENCE_DROPS.map((r) => (
                    <tr key={r.store}>
                      <td style={{ fontWeight: 600 }}>{r.store}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{r.chain}</td>
                      <td style={mono}>{r.prevTop1.toFixed(3)}</td>
                      <td style={{ ...mono, color: confidenceColor(r.currTop1) }}>{r.currTop1.toFixed(3)}</td>
                      <td style={{ ...mono, color: 'var(--danger)', fontWeight: 600 }}>{r.delta.toFixed(3)}</td>
                      <td style={mono}>{r.modsAffected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Newly Onboarded Stores */}
          <div style={{ opacity: visibility[4] ? 1 : 0, transform: visibility[4] ? 'translateY(0)' : 'translateY(8px)', transition: 'opacity 140ms ease, transform 140ms ease', marginTop: 20 }}>
            <div className="forge-card">
              <div className="forge-card-title">Newly Onboarded Stores</div>
              <table className="admin-table">
                <thead>
                  <tr><th>Store</th><th>Chain</th><th>Active Mods</th><th>Whitelisted UPCs</th><th>Date</th></tr>
                </thead>
                <tbody>
                  {NEW_STORES_ONBOARDED.map((r) => (
                    <tr key={r.store}>
                      <td style={{ fontWeight: 600 }}>{r.store}</td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{r.chain}</td>
                      <td style={mono}>{r.mods}</td>
                      <td style={mono}>{r.whitelisted.toLocaleString()}</td>
                      <td style={mono}>{r.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

    </div>
  );
};
